import Decimal           from 'decimal.js';
import Title_ru_RU_Class from "@/core/misc/decorators/Title_ru_RU_Class";

import Core              from '@/core/Core';
import Numeric           from '@/core/types/Numeric';
import Qty               from '@/core/types/Qty';
import Sum               from '@/core/types/Sum';
import Multiplier        from '@/core/types/Multiplier';

import type Currency     from '@/domains/Currencies/Currency';
import type Unit         from '@/domains/Units/Unit';
import type VatRate      from '@/core/types/VatRate';
import type DateSimple   from '@/core/types/DateSimple';
import type CurrencyRate from '@/core/types/CurrencyRate';

@Title_ru_RU_Class('Цена')
class Price extends Numeric {
  protected static readonly SCALE = 3;

  static fromServer(value: string): Price {
    return new Price(value);
  }

  /** Результат вычисления — уже посчитанный Decimal. */
  static fromOperation(value: Decimal): Price {
    return new Price(value);
  }

  /** Цена + цена = цена. Например, цена товара плюс доставка за единицу. */
  plus(other: Price | null | undefined): Price {
    return Price.fromOperation(this.value.plus(Price.toDecimal(other)));
  }

  /** Цена − цена = цена. Обратная к plus. */
  minus(other: Price | null | undefined): Price {
    return Price.fromOperation(this.value.minus(Price.toDecimal(other)));
  }

  /**
   * Та же цена, округлённая до двух знаков.
   *
   * Тип остаётся Price со своим scale 3 — меняется значение, а не тип:
   * 1.235 превращается в 1.240. Нужно там, где цена должна быть выражена
   * в целых копейках: часть покупателей не принимает третий знак.
   *
   * Режим округления берётся у типа (half-up), отдельного здесь нет:
   * расхождение с остальными расчётами фронта недопустимо.
   */
  roundTo2Decimals(): Price {
    return Price.fromOperation(Price.round(this.value, 2));
  }

  /**
   * Цена × количество = сумма (результат округлится до 2 знаков).
   * Цена × множитель = цена (наценка: 1.20 даёт +20%).
   * Цена × курс = цена (в другой валюте). (Используется в исключительном случае в платеже)
   *
   * Тип результата задаёт операнд: количество уводит в сумму, множитель
   * оставляет ценой.
   */
  mul(qty: Qty | null | undefined): Sum;
  mul(multiplier: Multiplier): Price;
  mul(rate: CurrencyRate): Price;
  mul(operand: Qty | Multiplier | CurrencyRate | null | undefined): Sum | Price {
    /** Тип результата задаёт операнд, но у пустого операнда типа нет.
     *  Трактуем пустоту как количество: цена × количество = сумма —
     *  это ходовой путь (позиция с очищенным количеством), а наценка
     *  пустым множителем смысла не имеет. */
    if (operand instanceof Qty || operand === null || operand === undefined) {
      return Sum.fromOperation(this.value.times(Price.toDecimal(operand)));
    } else {
      return Price.fromOperation(this.value.times(operand.value));
    }
  }

  /**
   * Полная цена (с НДС). Логика — в MoneyUtils.getFull (одна на фронт),
   * здесь только оборачиваем результат в Price.
   */
  getFull(isVatIncluded: boolean, vatRate: VatRate | null | undefined): Price {
    return Price.fromOperation(Core.utils.money.getFull(this.value, isVatIncluded, vatRate?.value));
  }

  /**
   * Цена без НДС — операция, обратная getFull. Логика — в MoneyUtils.getNet
   * (одна на фронт), здесь только оборачиваем результат в Price.
   */
  getNet(isVatIncluded: boolean, vatRate: VatRate | null | undefined): Price {
    return Price.fromOperation(Core.utils.money.getNet(this.value, isVatIncluded, vatRate?.value));
  }

  /**
   * НДС в этой цене. Логика — в MoneyUtils.getVat (одна на фронт),
   * здесь только оборачиваем результат в Price.
   */
  getVat(isVatIncluded: boolean, vatRate: VatRate | null | undefined): Price {
    return Price.fromOperation(Core.utils.money.getVat(this.value, isVatIncluded, vatRate?.value));
  }

  /**
   * Начислить НДС. Применимо к цене БЕЗ НДС.
   * Отличать от getVat: тот возвращает саму сумму налога, этот — цену с ним.
   */
  addVat(vatRate: VatRate): Price {
    return this.mul(vatRate.asMultiplier());
  }

  /**
   * Человекочитаемое представление цены: число + символ валюты + единица
   * измерения через "/". Оба постфикса опциональны. Ядро форматирования
   * числа — общий NumberUtils.formatDecimal; здесь поверх клеятся постфиксы
   * цены неразрывными пробелами (\u00A0), чтобы число и символ валюты /
   * единица не рвались по разным строкам.
   * Пустое (не введённое) значение печатается пустой строкой, а не "NaN".
   *
   * @param currency - валюта: объект или код (символ клеится постфиксом; null — без валюты)
   * @param unit     - единица измерения (имя клеится через "/"; null — без единицы)
   */
  format(currency: Currency | string | null = null, unit: Unit | null = null): string {
    if (this.isEmpty()) {
      return '';
    }

    let result = Core.utils.number.formatDecimal(this.value, this.scale);
    /** Валюта принимается и объектом, и кодом — в моделях она лежит
     *  по-разному. Неизвестный код даст null: печатаем число без символа,
     *  само значение от этого верным быть не перестаёт. */
    const currencyObject = Core.utils.money.resolveCurrency(currency);

    if (currencyObject) {
      result += `\u00A0${currencyObject.symbol}`;
    }

    if (unit) {
      result += `\u00A0/\u00A0${unit.name}`;
    }

    return result;
  }

  /**
   * Цена, приведённая к другой валюте. Логика — в MoneyUtils.convertSync
   * (одна на фронт), здесь только приведение валют к объектам.
   *
   * Сам Price валюту не несёт — исходная приходит параметром, наравне с
   * целевой. Обе принимаются и объектом, и кодом: в моделях валюта лежит
   * по-разному.
   *
   * Sync в имени — курс берётся из реактивного кеша, сервер не ждётся.
   * Осмысленный результат метод даёт лишь внутри реактивного контекста
   * (шаблон, computed): присвоенный в обычное поле, он не пересчитается
   * ни при появлении курса, ни при смене валюты.
   *
   * null — валюта неизвестна, то есть дефект данных. Пустая цена здесь не
   * годится: снаружи она неотличима от «не введено» и от «курса нет».
   * Отсутствие курса — другое дело, оно штатно и обрабатывается внутри
   * convertSync.
   *
   * @param from - валюта, в которой выражена эта цена (объект или код)
   * @param to   - валюта, в которую конвертируем (объект или код)
   * @param date - дата курса
   */
  convertSync(from: Currency | string, to: Currency | string, date: Date | DateSimple): Price | null {
    const fromCurrency = Core.utils.money.resolveCurrency(from);
    const toCurrency   = Core.utils.money.resolveCurrency(to);

    if (fromCurrency === null || toCurrency === null) {
      Core.error(`Price.convertSync: неизвестная валюта (${from} → ${to})`);
      return null;
    } else {
      return Core.utils.money.convertSync(this, fromCurrency, toCurrency, date);
    }
  }

  /**
   * Цена, приведённая к системной валюте отображения, готовой строкой.
   *
   * Сам Price валюту не несёт — валюта, в которой он выражен, приходит
   * параметром: объектом Currency или просто кодом. Целевая валюта не
   * параметр: она системная, одна на приложение, берётся из ядра.
   *
   * Дата курса обязательна и передаётся явно. Умолчания у неё нет
   * сознательно: у каждого документа своя дата курса (тендер приводится к
   * дате начала, отгрузка к своей), и забытый параметр дал бы не заглушку,
   * а правдоподобное неверное число без всякого признака ошибки. Там, где
   * текущая дата верна по смыслу (прайсы, актуальные оферы), пишется
   * DateSimple.today() — это видно в месте вызова.
   *
   * Метод синхронный и предназначен только для вывода. Курс читается из
   * реактивного кеша, поэтому осмысленный результат он даёт лишь внутри
   * реактивного контекста (шаблон, computed): присвоенный в обычное поле,
   * он не пересчитается ни при появлении курса, ни при смене валюты.
   *
   * Логика конвертации — в MoneyUtils.convertSync (одна на фронт), здесь
   * только форматирование результата.
   *
   * @param from - валюта, в которой выражена эта цена (объект или код)
   * @param date - дата курса
   * @param unit - единица измерения (клеится через "/"; null — без единицы)
   */
  formatInDisplayCurrency(
    from: Currency | string,
    date: Date | DateSimple,
    unit: Unit | null = null
  ): string {
    // Пустое (не введённое) значение печатается пустой строкой независимо от курса
    if (this.isEmpty()) {
      return '';
    }

    const fromCurrency    = Core.utils.money.resolveCurrency(from);
    const displayCurrency = Core.utils.money.displayCurrency;

    if (fromCurrency === null || displayCurrency === null) {
      return '';
    }

    const fallbackMark = Core.utils.money.getRateFallbackMark(fromCurrency, displayCurrency, date);

    if (fallbackMark !== null) {
      return fallbackMark;
    } else {
      return Core.utils.money.convertSync(this, fromCurrency, displayCurrency, date).format(displayCurrency, unit);
    }
  }

  /**
   * Цена / цена = множитель (отношение двух цен).
   * Например: текущая максимальная цена / цена прошлого заказа = коэффициент
   * изменения цены, из которого asPercent даёт расхождение в процентах.
   *
   * Отдельным именем, а не div: деление цены на цену даёт множитель, а не
   * цену, и общего имени с делением на множитель у него быть не должно.
   * Зеркало Sum.divBySum.
   *
   * Делитель нулевым быть не должен: decimal.js на делении на ноль не бросает,
   * а даёт Infinity, и конструктор Multiplier его пропустит. Проверка isZero
   * остаётся на месте вызова — там же, где известно, чем считать отсутствие
   * базы сравнения.
   */
  divByPrice(other: Price | null | undefined): Multiplier {
    return Multiplier.fromOperation(this.value.dividedBy(Price.toDecimal(other)));
  }
}

export default Price;
